<template>
	<section class="social-login__guts">
		<CCallout v-if="requiredUser && isInvitation" type="info">
			<p>Please continue as {{ requiredUser }} to accept this invitation.</p>
		</CCallout>
		<CCallout v-else-if="requiredUser" type="info">
			<p>Please log in as {{ requiredUser }} to access this entry.</p>
		</CCallout>
		<CCallout v-else-if="requiredOrganization" type="info">
			<p>Please continue to access {{ requiredOrganization ?? 'this organization' }}.</p>
		</CCallout>
		<ExternalAuthButton
			v-if="!requiredAuthProvider || requiredAuthProvider === 'Google'"
			ref="google"
			class="auth-wrapper__button"
			:return-url="returnUrl"
			:provider="requiredAuthProvider || 'Google'"
			:button-text="buttonPrefix + ' with Google'"
			:authentication-action="authenticationAction"
			:auth-method-type="requiredAuthMethod || 'Google'"
			:required-org="requiredOrgCode"
			:use-redirect="isMobile"
			@authenticated="handleSocialAuthentication($event, AuthenticationMethodType.Google)"
		/>
		<ExternalAuthButton
			v-if="!requiredAuthProvider || requiredAuthProvider === 'Facebook'"
			ref="facebook"
			:return-url="returnUrl"
			class="auth-wrapper__button"
			:provider="requiredAuthProvider || 'Facebook'"
			:button-text="buttonPrefix + ' with Facebook'"
			:authentication-action="authenticationAction"
			:auth-method-type="requiredAuthMethod || 'Facebook'"
			:required-org="requiredOrgCode"
			:use-redirect="isMobile"
			@authenticated="handleSocialAuthentication($event, AuthenticationMethodType.Facebook)"
		/>
		<ExternalAuthButton
			v-if="!requiredAuthProvider || requiredAuthProvider === 'Microsoft'"
			ref="microsoft"
			:return-url="returnUrl"
			class="auth-wrapper__button"
			:provider="requiredAuthProvider || 'Microsoft'"
			:button-text="buttonPrefix + ' with Microsoft'"
			:authentication-action="authenticationAction"
			:auth-method-type="requiredAuthMethod || 'Microsoft'"
			:required-org="requiredOrgCode"
			:use-redirect="isMobile"
			@authenticated="handleSocialAuthentication($event, AuthenticationMethodType.Microsoft)"
		/>
		<ExternalAuthButton
			v-if="requiredAuthProvider === 'OpenIdConnect'"
			ref="openidconnect"
			:return-url="returnUrl"
			class="auth-wrapper__button"
			:provider="requiredAuthProvider || 'OpenIdConnect'"
			:button-text="buttonPrefix + ' with SSO'"
			:authentication-action="authenticationAction"
			:auth-method-type="requiredAuthMethod || 'Custom'"
			:required-org="requiredOrgCode"
			use-redirect
			@authenticated="handleSocialAuthentication($event, AuthenticationMethodType.Custom)"
		/>
		<CCallout v-if="authResult && authResult.AccountNuked" type="error">
			<p>We couldn't find a Cognito Forms account for {{ authResult.UserEmail ?? 'you' }}.</p>
		</CCallout>
		<CCallout v-else-if="authResult && authResult.WrongUser" type="error">
			<p>Please log in as {{ requiredUser }} to access {{ requiredOrganization ?? 'this organization' }}.</p>
		</CCallout>
		<CCallout v-else-if="authResult && authResult.MissingClaim" type="error">
			<p>Your identity provider did not supply the {{ authResult.MissingClaim }}. Please contact your administrator.</p>
		</CCallout>
		<CCallout v-else-if="authResult && authResult.SsoError" type="error">
			<p>There is an SSO configuration issue. Please contact customer support.</p>
		</CCallout>
		<div class="social-login__form">
			<div class="strike">
				<span>or</span>
			</div>
			<ValidationObserver ref="observer" @submit.prevent="onSubmit">
				<CValidatedInput
					v-model.trim="email"
					vid="email-address"
					placeholder="Email Address"
					name="Email Address"
					autocomplete="on"
					type="email"
					rules="required|email"
					class="mb-4"
					:disabled="requiredUser"
					@keydown.enter.native="onSubmit"
					@blur="validate"
				/>
				<!-- This is just here so firefox remembers your email -->
				<input type="password" hidden />
				<CCallout v-if="authResult?.UserLocked" type="error">
					<p>This account has been locked. Please try again in one hour.</p>
				</CCallout>
				<CCallout v-if="authResult?.NotAMember || isNotAMember" type="error">
					<p>You are not a member of {{ requiredOrganization ?? 'this organization' }}.</p>
				</CCallout>
				<CCallout v-if="authResult?.UserDoesNotExist" type="error">
					<p>
						We couldn't find an account for {{ authResult?.UserEmail ?? 'you' }}.
						<br>
						<button class="button--anchor text-primary" title="Sign up for Cognito Forms" @click="redirectToSignup">Would you like to sign up</button>?
					</p>
				</CCallout>
				<CCallout v-if="authResult?.InactiveGuest" type="error" size="s">
					You are not an active guest of {{ requiredOrganization }}.
				</CCallout>
				<CStatusButton
					class="button button--primary text-4"
					:status="buttonStatusInternal"
					@click.native="onSubmit"
				>
					{{ buttonPrefix }} with Email
				</CStatusButton>
			</ValidationObserver>
		</div>
	</section>
</template>
<script lang="ts">
	import { computed, defineComponent, ref, watch, nextTick } from 'vue';
	import ExternalAuthButton from 'src/components/auth/ExternalAuthButton.vue';
	import type { ButtonStatus } from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import type { PropType } from 'vue';
	import CValidatedInput from '@cognitoforms/shared-components/src/components/form-controls/ValidatedInput.vue';
	import CCallout from '@cognitoforms/shared-components/src/components/content/Callout.vue';
	import { AuthenticationMethodType } from '@cognitoforms/types/server-types/model/authentication-method-type';
	import type { RestartAuthenticationResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/restart-authentication-result';
	import { ValidationObserver } from 'vee-validate';
	import { isDeviceType } from '@cognitoforms/utils/user-agent';

	export default defineComponent({
		name: 'CAuthStart',
		components: {
			ExternalAuthButton,
			CValidatedInput,
			CStatusButton,
			CCallout,
			ValidationObserver
		},
		props: {
			isSignup: {
				type: Boolean,
				default: false
			},
			requiredAuthMethod: {
				type: String,
				default: null
			},
			requiredAuthProvider: {
				type: String,
				default: null
			},
			returnUrl: {
				type: String,
				default: null
			},
			buttonStatus: {
				type: String as PropType<ButtonStatus>,
				default: 'default'
			},
			authResult: {
				type: Object as PropType<RestartAuthenticationResult>,
				default: () => {}
			},
			requiredOrganization: {
				type: String,
				default: null
			},
			requiredOrgCode: {
				type: String,
				default: null
			},
			requiredUser: {
				type: String,
				default: null
			},
			isInvitation: {
				type: Boolean,
				default: null
			},
			isNotAMember: {
				type: Boolean,
				default: null
			},
			autoSocialSignup: {
				type: Boolean,
				default: false
			}
		},
		emits: ['email-authentication', 'social-authentication', 'redirect-to-signup', 'email-input'],
		setup(props, { emit }) {
			const isMobile = isDeviceType(window.navigator, 'iphone|ipod|ipad|android|blackberry|windows-phone');

			const email = ref(props.authResult?.UserEmail || props.requiredUser || '');
			watch(email, () => {
				emit('email-input', email.value);
			});

			const authenticationAction = computed<string>(() => props.isSignup ? 'signup' : 'login');

			const buttonPrefix = computed(() => {
				return props.isSignup ? 'Sign Up' : props.requiredOrganization ? 'Continue' : 'Log In';
			});

			const observer = ref<InstanceType<typeof ValidationObserver>>(null);
			const emailError = ref(false);

			const validate = async () => {
				const valid = await observer.value.validate();
				emailError.value = !valid;
				return valid;
			};

			const onSubmit = async () => {
				const valid = await validate();
				if (!valid)
					return;

				emit('email-authentication', email.value);
			};

			watch(email, () => {
				emailError.value = false;
			});

			const buttonStatusInternal = computed<ButtonStatus>(() => {
				if (emailError.value || props.authResult?.UserLocked)
					return 'disabled';

				return props.buttonStatus;
			});

			const google = ref<typeof ExternalAuthButton>(null);
			const facebook = ref<typeof ExternalAuthButton>(null);
			const microsoft = ref<typeof ExternalAuthButton>(null);
			const openidconnect = ref<typeof ExternalAuthButton>(null);

			watch(() => props.autoSocialSignup, async (val) => {
				if (!val)
					return;

				await nextTick();

				switch (props.authResult?.AuthenticationMethod) {
				case AuthenticationMethodType.Google:
					google.value.handleClick();
					break;
				case AuthenticationMethodType.Facebook:
					facebook.value.handleClick();
					break;
				case AuthenticationMethodType.Microsoft:
					microsoft.value.handleClick();
					break;
				case AuthenticationMethodType.Custom:
					openidconnect.value.handleClick();
					break;
				default:
					break;
				}
			});

			const handleSocialAuthentication = async (authenticationToken, authMethodType: AuthenticationMethodType) => {
				emit('social-authentication', authenticationToken, authMethodType);
			};
			const redirectToSignup = () => {
				emit('redirect-to-signup', email.value);
			};

			return {
				isMobile,
				email,
				buttonPrefix,
				authenticationAction,
				buttonStatusInternal,
				onSubmit,
				validate,
				redirectToSignup,
				handleSocialAuthentication,
				AuthenticationMethodType,
				observer,
				google,
				facebook,
				microsoft,
				openidconnect
			};
		}
	});
</script>